import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './VerifyEmail.css';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState(
    searchParams.get('email') || localStorage.getItem('registrationEmail') || ''
  );
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Check if verification via link (token-based)
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyViaLink(token);
    }
  }, [searchParams]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const verifyViaLink = async (token) => {
    if (!email) {
      setError('Email nicht gefunden. Bitte versuche erneut zu registrieren.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.code === 'VERIFICATION_EXPIRED') {
          setError('Verification-Link ist abgelaufen. Fordere einen neuen Code an.');
        } else {
          setError(data.message || 'Verification fehlgeschlagen');
        }
      } else {
        setSuccess('✅ Email erfolgreich verifiziert! Du wirst zum Login weitergeleitet...');
        localStorage.removeItem('registrationEmail');
        setTimeout(() => navigate('/?login=true'), 2000);
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();

    if (!email || !validEmail(email)) {
      setError('Ungültige Email-Adresse');
      return;
    }

    if (!code || code.length !== 6) {
      setError('Bitte gib einen 6-stelligen Code ein');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.code === 'VERIFICATION_EXPIRED') {
          setError('Code ist abgelaufen. Fordere einen neuen an.');
        } else {
          setError(data.message || 'Verification fehlgeschlagen');
        }
      } else {
        setSuccess('✅ Email erfolgreich verifiziert! Du wirst zum Login weitergeleitet...');
        localStorage.removeItem('registrationEmail');
        setTimeout(() => navigate('/?login=true'), 2000);
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email || !validEmail(email)) {
      setError('Ungültige Email-Adresse');
      return;
    }

    setResendDisabled(true);
    setResendTimer(60);
    setError('');

    try {
      const res = await fetch('/api/auth/send-verification-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Fehler beim Code-Versand');
        setResendDisabled(false);
        setResendTimer(0);
      } else {
        setSuccess('📧 Neuer Code wurde versendet!');
        setCode('');
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
      setResendDisabled(false);
      setResendTimer(0);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
    setSuccess('');
  };

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
  };

  const validEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-container">
        <div className="verify-header">
          <div className="verify-icon">📧</div>
          <h1>Email-Adresse verifizieren</h1>
          <p>Wir haben dir einen Verifizierungscode gesendet</p>
        </div>

        <div className="verify-content">
          {/* Success Message */}
          {success && (
            <div className="message success">
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="message error">
              ⚠️ {error}
            </div>
          )}

          {/* Email Input */}
          <div className="form-group">
            <label htmlFor="email">Email-Adresse</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="deine@email.de"
              disabled={loading}
              className="email-input"
            />
          </div>

          {/* Code Input */}
          <form onSubmit={handleCodeSubmit} className="verify-form">
            <div className="form-group">
              <label htmlFor="code">Verifizierungscode</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                maxLength="6"
                disabled={loading}
                className="code-input"
                autoComplete="off"
              />
              <p className="code-hint">
                Gib die 6-stellige Nummer aus der Email-Nachricht ein
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="btn-verify"
            >
              {loading ? '⏳ Wird überprüft...' : '✓ Verifizieren'}
            </button>
          </form>

          {/* Divider */}
          <div className="divider">
            <span>oder</span>
          </div>

          {/* Resend Code */}
          <div className="resend-section">
            <p>Hast du keinen Code erhalten?</p>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendDisabled || loading}
              className="btn-resend"
            >
              {resendTimer > 0
                ? `⏱ ${resendTimer}s warten`
                : '🔄 Code erneut versenden'}
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="verify-footer">
          <p>
            Der Code ist 24 Stunden gültig. Nach der Verifizierung kannst du dich einloggen.
          </p>
          <p>
            <a href="/?login=true" className="link-back">
              ← Zurück zum Login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
